import type { PropsWithChildren, ReactNode } from 'react'

import { FieldLabelIconStyled } from './styles/FieldLabelIconStyled'
import { FieldStyled } from './styles/FieldStyled'
import { LabelBadgeStyled } from './styles/LabelBadgeStyled'
import { FieldHelperText } from '../../elements/FieldHelperText'
import { InputLabel } from '../Form/InputLabel'
import {
  InfoBadge,
  type InfoBadgeType,
  InfoBadgeTypes,
} from '@ui/elements/Badge'
import { type IconElement, renderIcon } from '@ui/elements/Icon'
import { WithTooltip } from '@ui/elements/Tooltip'

export interface IFieldLabelBadge {
  text: string
  type?: InfoBadgeType
}

export interface IFieldProps {
  layout?: 'horizontal' | 'vertical'
  label?: string | ReactNode
  labelId?: string
  labelFor?: string
  labelTooltipMessage?: string | ReactNode
  /**
   * The label icon's accessible name. Defaults to `labelTooltipMessage` when
   * that is a string.
   */
  labelTooltipAriaLabel?: string
  labelIcon?: IconElement
  labelBadge?: IFieldLabelBadge
  helperText?: string
  error?: boolean
  required?: boolean
}

export const Field = ({
  layout = 'vertical',
  label,
  labelId,
  labelFor,
  labelTooltipMessage,
  labelIcon,
  labelBadge,
  labelTooltipAriaLabel,
  helperText,
  error,
  required,
  children,
  ...otherProps
}: PropsWithChildren<IFieldProps>) => {
  const labelBadgeElement = labelBadge && (
    <>
      {required && (
        <span className='formLabelAsterisk' aria-hidden>
          {' *'}
        </span>
      )}
      <LabelBadgeStyled>
        <InfoBadge
          type={labelBadge.type ?? InfoBadgeTypes.LOW_PRIORITY}
          text={labelBadge.text}
          size='small'
        />
      </LabelBadgeStyled>
    </>
  )

  // bypass MUI's behavior of appending its asterisk after the badge
  const showMuiAsterisk = required && !labelBadge

  return (
    <FieldStyled layout={layout} {...otherProps}>
      {label &&
        (labelIcon ? (
          // The row carries the label styles and the asterisk; only the text
          // is the <label>, so the icon stays out of the control's name and
          // click target.
          <InputLabel component='div' error={error} required={showMuiAsterisk}>
            <InputLabel
              id={labelId}
              htmlFor={labelFor}
              error={error}
              display='inline'
            >
              {label}
            </InputLabel>

            <WithTooltip
              title={labelTooltipMessage}
              placement='top'
              isDisabled={!labelTooltipMessage}
            >
              <FieldLabelIconStyled
                tabIndex={labelTooltipMessage ? 0 : undefined}
              >
                {renderIcon(labelIcon, {
                  ariaLabel:
                    labelTooltipAriaLabel ??
                    (typeof labelTooltipMessage === 'string'
                      ? labelTooltipMessage
                      : undefined),
                })}
              </FieldLabelIconStyled>
            </WithTooltip>

            {labelBadgeElement}
          </InputLabel>
        ) : (
          <InputLabel
            id={labelId}
            htmlFor={labelFor}
            error={error}
            required={showMuiAsterisk}
          >
            {label}

            {labelBadgeElement}
          </InputLabel>
        ))}

      <div>
        {children}

        {helperText && (
          <FieldHelperText error={error}>{helperText}</FieldHelperText>
        )}
      </div>
    </FieldStyled>
  )
}
